import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { RuntimeConfigService } from '../config/runtime-config.service';
import { LoggerService } from '../utils/logger.service';

/**
 * Catch 401 from our own backend, run one Supabase refresh, then retry the
 * original request with the new access token. Only kicks in for Atplane API
 * URLs and only when a Bearer header is already present (otherwise we'd loop
 * for callers that genuinely don't have a session).
 */
@Injectable({ providedIn: 'root' })
export class AuthRefreshInterceptor implements HttpInterceptor {
  // Avoid eager construction loops:
  //   RestService -> RuntimeConfigService (OK), HttpClient -> interceptors -> AuthService -> RestService.
  // We pull `AuthService` lazily on the first 401.
  constructor(
    private injector: Injector,
    private runtimeConfig: RuntimeConfigService,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isApiCall(req.url) || !req.headers.has('Authorization')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
          return throwError(() => error);
        }
        const auth = this.injector.get(AuthService);
        LoggerService.log('[auth-refresh] 401 on API; attempting silent refresh', { url: req.url });
        return from(auth.refreshAccessTokenIfPossible()).pipe(
          switchMap((ok) => {
            if (!ok) {
              return throwError(() => error);
            }
            const newSid = auth.getUser()?.sid;
            if (!newSid) {
              return throwError(() => error);
            }
            const retried = req.clone({
              setHeaders: { Authorization: `Bearer ${newSid}` },
            });
            return next.handle(retried);
          }),
        );
      }),
    );
  }

  private isApiCall(url: string): boolean {
    const base = this.runtimeConfig.apiBaseUrl;
    if (!base) return false;
    if (url.startsWith(base)) return true;
    // Cover the relative `/api/...` case used by `ng serve` proxy in dev.
    if (base === '/api' && url.startsWith('/api/')) return true;
    return false;
  }
}
